import { useEffect } from 'react';

import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';
import { TilePlaceholder } from 'pages/dashboard/components';
import type { PartnerVendorInfo } from 'apis/DashboardApi';
import { Card, Image, ListGroup } from 'react-bootstrap';
import { DashboardApi } from 'apis/DashboardApi';
import { Link } from 'react-router-dom';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

type VendorConfig = {
    key: string;
    links: { appendTitle?: boolean; href: string; label: MessageDescriptor; target?: string }[];
    logo?: string;
    title: MessageDescriptor;
};

const MESSAGES = {
    codingInMinecraft: { id: 'coding_in_minecraft.f19ed2c0', defaultMessage: 'Coding In Minecraft' },
    courseListAndHours: { id: 'course_list_and_hours.0114e812', defaultMessage: 'Course List and Hours' },
    courseResources: { id: 'course_resources.96e8f82d', defaultMessage: 'Course Resources' },
    gettingStartedWith: { id: 'getting_started_with.7fd25331', defaultMessage: 'Getting Started With' },
    quickStartGuide: { id: 'quick_start_guide.53e8d6c6', defaultMessage: 'Quick Start Guide' }
};

const VENDOR_CONFIGS: VendorConfig[] = [
    {
        key: 'learnkey',
        links: [
            { appendTitle: true, href: 'https://educationsolutions.learnkey.com/', label: MESSAGES.gettingStartedWith, target: '_blank' },
            { href: '/course-resources', label: MESSAGES.courseResources },
            { href: 'https://educationsolutions.learnkey.com/pdfs/Course_List_with_Class_Hours.pdf', label: MESSAGES.courseListAndHours, target: '_blank' }
        ],
        logo: 'lklogo_footer_2015.png',
        title: { id: 'learnkey_course_resources.d2607e2a', defaultMessage: 'LearnKey Course Resources' }
    },
    {
        key: 'interactiva',
        links: [
            { appendTitle: true, href: 'http://www.gmetrix.net/documentation/documents/Course_QS_Guide.pdf', label: MESSAGES.gettingStartedWith, target: '_blank' }
        ],
        title: { id: 'media_interactiva_course_resources.eda4865f', defaultMessage: 'Media Interactiva Course Resources' }
    },
    {
        key: 'brainbuffet',
        links: [
            { appendTitle: true, href: 'http://www.gmetrix.net/documentation/documents/Course_QS_Guide.pdf', label: MESSAGES.gettingStartedWith, target: '_blank' },
            { href: '/course-resources', label: MESSAGES.courseResources }
        ],
        logo: 'BrainBuffetGreyscaleIcon.png',
        title: { id: 'brain_buffet_resources.38029c84', defaultMessage: 'Brain Buffet Resources' }
    },
    {
        key: 'minecraft',
        links: [
            { href: '/course-resources', label: MESSAGES.courseResources },
            { href: 'https://codingcredentials.com/cimquickstart/', label: MESSAGES.quickStartGuide, target: '_blank' }
        ],
        logo: 'CodingInMinecraftGreyscaleIcon.png',
        title: { id: 'coding_in_minecraft.f19ed2c0', defaultMessage: 'Coding In Minecraft' }
    },
    {
        key: 'pearson',
        links: [
            { appendTitle: true, href: 'http://www.gmetrix.net/documentation/documents/Course_QS_Guide.pdf', label: MESSAGES.gettingStartedWith, target: '_blank' },
            { href: '/course-resources', label: MESSAGES.courseResources }
        ],
        logo: 'Pearson-Logo.png',
        title: { id: 'pearson_resources.7007767b', defaultMessage: 'Pearson Resources' }
    }
];

export const VendorPanelsTile = () => {

    const [state, setState] = useSuperState({
        data: null as PartnerVendorInfo | null,
        loading: true
    });
    const { data, loading } = state;

    const { selectedPartner, userId } = useApp();
    const { formatMessage } = useIntl();

    const loadData = async () => {

        try {

            const vendorInfo = await DashboardApi.GetPartnerVendorInfo(userId, selectedPartner.id);
            setState({ data: vendorInfo, loading: false });

        } catch {

            setState({ loading: false });

        }

    };

    useEffect(() => {

        loadData();

    }, []);

    const VendorPanel = (props: { config: VendorConfig }) => {

        const { config } = props;

        return (
            <Card className={'overflow-hidden'} key={config.key}>
                <Card.Header className="text-uppercase">
                    <strong>
                        {formatMessage(config.title)}
                    </strong>
                </Card.Header>
                <div className={'d-flex'}>
                    <ListGroup className={'flex-fill border-end'} variant="flush">
                        {config.links.map(link => {

                            const content = (
                                <>
                                    <i className="fa-solid fa-arrow-up-right-from-square me-2 text-muted" />
                                    {formatMessage(link.label)}
                                    {link.appendTitle ? ` ${formatMessage(config.title)}` : ''}
                                </>
                            );

                            return link.target
                                ? (
                                    <ListGroup.Item
                                        action
                                        href={link.href}
                                        key={link.href}
                                        rel="noreferrer"
                                        target={link.target}
                                    >
                                        {content}
                                    </ListGroup.Item>
                                )
                                : (
                                    <ListGroup.Item
                                        action
                                        as={Link}
                                        key={link.href}
                                        to={link.href}
                                    >
                                        {content}
                                    </ListGroup.Item>
                                );

                        })}
                    </ListGroup>
                    <div
                        style={{
                            flexShrink: 0,
                            width: 150
                        }}
                        className={'p-2 d-flex align-items-center justify-content-center bg-light'}
                    >
                        <Image
                            fluid
                            src={`${location.origin}/Images/HomeTab/${config.logo}`}
                        />
                    </div>
                </div>

            </Card>
        );

    };

    const JasperPanel = () => {

        const jasper = data?.jasper;

        return (
            <Card className={'overflow-hidden'}>
                <Card.Header className="text-uppercase">
                    <strong>
                        {<FormattedMessage defaultMessage="Jasperactive Course Resources" id="jasperactive_course_resources.6d2d8707" />}
                    </strong>
                </Card.Header>
                <div className={'d-flex'}>
                    <ListGroup className={'flex-fill border-end'} variant="flush">
                        <ListGroup.Item>
                            <i className="fa-solid fa-arrow-up-right-from-square me-2 text-muted" />
                            {<FormattedMessage defaultMessage="Getting Started With" id="getting_started_with.7fd25331" />}
                            {' Jasperactive'}
                        </ListGroup.Item>
                        <ListGroup.Item className={!jasper?.Successful ? 'bg-warning-subtle text-warning-emphasis' : ''}>
                            {jasper?.Successful
                                ? <span dangerouslySetInnerHTML={{ __html: `Your teacher code for ${selectedPartner?.name ?? ''} is: <b>${jasper.MsgSuccess}</b>` }} />
                                : (
                                    <span>
                                        {jasper?.MsgSuccess}
                                    </span>
                                )}
                        </ListGroup.Item>
                    </ListGroup>
                    <div className={'p-2 d-flex align-items-center justify-content-center bg-light'} style={{ flexShrink: 0, width: 150 }}>
                        <Image
                            fluid
                            src={`${location.origin}/Images/HomeTab/LogoJasperActiveGrey.png`}
                        />
                    </div>
                </div>
            </Card>
        );

    };

    const vendorList = (data?.vendors ?? []).map(v => v.toLowerCase());
    const matchedVendors = VENDOR_CONFIGS.filter(config => vendorList.some(v => v.includes(config.key)));

    return (
        <>

            { loading ?
                (
                    <>
                        {[1, 2, 3].map(key => <TilePlaceholder icon key={key} />)}
                    </>
                )
                :
                (
                    <>
                        {data ?
                            (
                                <>
                                    {!!data?.jasper && <JasperPanel />}

                                    {!!matchedVendors.length && matchedVendors.map((config, key) => (
                                        <VendorPanel config={config} key={key} />
                                    ))}
                                </>
                            )
                            :
                            (
                                <></>
                            )
                        }

                    </>

                )}

        </>
    );

};
